<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import conversationAPI from 'dashboard/api/inbox/conversation';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const DEFAULT_COLUMNS = [
  'Novo lead',
  'Em qualificação',
  'Aguardando conta de luz',
  'Qualificado',
  'Enviado ao vendedor',
  'Follow-up',
  'Perdido',
];

const DEFAULT_STAGE_KEYS = [
  'lead_stage',
  'kanban_stage',
  'pipeline_stage',
  'etapa_lead',
  'etapa',
  'automation_status',
  'flow_status',
  'current_status',
  'next_status',
];
const DEFAULT_SCORE_KEYS = ['score'];
const DEFAULT_QUALIFIED_KEYS = ['is_qualified', 'qualified'];
const DEFAULT_SOURCE_KEYS = ['source', 'origem'];
const DEFAULT_PHONE_KEYS = ['whatsapp_phone', 'phone'];
const MAX_PAGES = 50;
const DEFAULT_COLUMN_ALIASES = {
  'Novo lead': ['novo lead', 'new lead', 'lead', 'novo', 'new'],
  'Em qualificação': [
    'em qualificacao',
    'em qualificação',
    'qualificacao',
    'qualificação',
    'waiting_menu_option',
    'waiting_installation_type',
    'waiting_ownership',
    'waiting_energy_connection',
    'waiting_average_bill',
    'waiting_payment_method',
    'qualifying',
    'in qualification',
    'qualification',
  ],
  'Aguardando conta de luz': [
    'aguardando conta de luz',
    'aguardando conta',
    'conta de luz',
    'aguardando_documento',
    'waiting_for_bill',
    'waiting utility bill',
    'waiting bill',
    'utility bill',
  ],
  Qualificado: ['qualificado', 'qualified'],
  'Enviado ao vendedor': [
    'enviado ao vendedor',
    'enviado_vendedor',
    'sent_to_sales',
    'sent to sales',
    'chatwoot_handoff_created',
    'handoff_created',
    'lead_qualified_handoff',
    'lead_qualified',
    'sales handoff',
    'handoff',
  ],
  'Follow-up': [
    'follow-up',
    'follow up',
    'followup',
    'retorno',
    'acompanhamento',
  ],
  Perdido: ['perdido', 'lost', 'closed lost', 'closed_lost', 'unqualified'],
};
const STATUS_LABELS = {
  lead_created: 'Novo lead',
  open: 'Aberto',
  pending: 'Pendente',
  resolved: 'Resolvido',
  snoozed: 'Adiado',
  qualified: 'Qualificado',
  unqualified: 'Não qualificado',
  waiting_menu_option: 'Em qualificação',
  waiting_installation_type: 'Em qualificação',
  waiting_ownership: 'Em qualificação',
  waiting_energy_connection: 'Em qualificação',
  waiting_average_bill: 'Em qualificação',
  waiting_payment_method: 'Em qualificação',
  waiting_electric_bill: 'Aguardando conta de luz',
  lead_qualified: 'Qualificado',
  chatwoot_handoff_created: 'Enviado ao vendedor',
  followup: 'Follow-up',
  'follow-up': 'Follow-up',
  'follow up': 'Follow-up',
  lost: 'Perdido',
  closed_lost: 'Perdido',
  'closed lost': 'Perdido',
};
const OPENABLE_STAGE_LABELS = ['Qualificado', 'Enviado ao vendedor'];

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountId, currentAccount } = useAccount();

const agents = useMapGetter('agents/getVerifiedAgents');

const isLoading = ref(false);
const conversations = ref([]);
const totalLeads = ref(0);

const filters = reactive({
  search: '',
  status: '',
  assigneeId: '',
  qualified: 'all',
  scoreMin: '',
  scoreMax: '',
  dateFrom: '',
  dateTo: '',
});

const normalizeValue = value =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const slugify = value =>
  normalizeValue(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const accountKanbanConfig = computed(() => {
  const customAttributes = currentAccount.value?.custom_attributes || {};
  return customAttributes.lead_kanban_config || {};
});

const getConfiguredKeys = (configKey, fallback) => {
  const configuredValue = accountKanbanConfig.value?.[configKey];

  if (Array.isArray(configuredValue) && configuredValue.length) {
    return configuredValue.filter(Boolean);
  }

  if (typeof configuredValue === 'string' && configuredValue.trim()) {
    return configuredValue
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  return fallback;
};

const parseColumns = rawValue => {
  const normalizedValue =
    rawValue || currentAccount.value?.custom_attributes?.lead_kanban_columns;

  if (Array.isArray(normalizedValue)) {
    return normalizedValue;
  }

  if (typeof normalizedValue === 'string' && normalizedValue.trim()) {
    try {
      const parsedValue = JSON.parse(normalizedValue);
      if (Array.isArray(parsedValue)) {
        return parsedValue;
      }
    } catch (error) {
      return normalizedValue
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }
  }

  return DEFAULT_COLUMNS;
};

const kanbanColumns = computed(() => {
  const rawColumns = parseColumns(accountKanbanConfig.value?.columns);
  return rawColumns.map((column, index) => {
    if (typeof column === 'string') {
      return {
        id: slugify(column) || `stage-${index + 1}`,
        label: column,
        aliases: [
          normalizeValue(column),
          ...(DEFAULT_COLUMN_ALIASES[column] || []).map(item =>
            normalizeValue(item)
          ),
        ],
      };
    }

    const label = column.label || column.name || DEFAULT_COLUMNS[index];
    const aliases = [
      normalizeValue(label),
      ...((DEFAULT_COLUMN_ALIASES[label] || []).map(item => normalizeValue(item))),
      ...((column.aliases || []).map(item => normalizeValue(item)).filter(
        Boolean
      )),
    ];

    return {
      id: column.id || slugify(label) || `stage-${index + 1}`,
      label,
      aliases,
    };
  });
});

const stageKeys = computed(() =>
  getConfiguredKeys('stage_attribute_keys', DEFAULT_STAGE_KEYS)
);
const scoreKeys = computed(() =>
  getConfiguredKeys('score_attribute_keys', DEFAULT_SCORE_KEYS)
);
const qualifiedKeys = computed(() =>
  getConfiguredKeys('qualified_attribute_keys', DEFAULT_QUALIFIED_KEYS)
);
const sourceKeys = computed(() =>
  getConfiguredKeys('source_attribute_keys', DEFAULT_SOURCE_KEYS)
);
const phoneKeys = computed(() =>
  getConfiguredKeys('phone_attribute_keys', DEFAULT_PHONE_KEYS)
);

const getAttributeCandidates = conversation => {
  const conversationAttributes = conversation.custom_attributes || {};
  const contactAttributes = conversation.meta?.sender?.custom_attributes || {};

  return {
    conversationAttributes,
    contactAttributes,
  };
};

const getAttributeValue = (conversation, keys = []) => {
  const { conversationAttributes, contactAttributes } =
    getAttributeCandidates(conversation);

  for (const key of keys) {
    if (conversationAttributes[key] !== undefined) {
      return conversationAttributes[key];
    }
    if (contactAttributes[key] !== undefined) {
      return contactAttributes[key];
    }
  }

  return undefined;
};

const parseBoolean = value => {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined || value === '') return null;

  const normalized = normalizeValue(value);
  if (
    ['true', '1', 'yes', 'sim', 'qualified', 'qualificado'].includes(
      normalized
    )
  ) {
    return true;
  }

  if (
    ['false', '0', 'no', 'nao', 'não', 'unqualified', 'perdido'].includes(
      normalized
    )
  ) {
    return false;
  }

  return null;
};

const parseScore = value => {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getLeadStatusValue = conversation =>
  getAttributeValue(conversation, [
    'lead_status',
    'status',
    'lead_stage',
    'kanban_stage',
    'pipeline_stage',
    'etapa_lead',
    'etapa',
    'automation_status',
    'flow_status',
    'current_status',
    'next_status',
    'handoff_reason',
    'event_name',
    'event_type',
  ]) || conversation.status;

const resolveQualifiedFlag = conversation => {
  const explicitQualified = parseBoolean(
    getAttributeValue(conversation, qualifiedKeys.value)
  );

  if (explicitQualified !== null) {
    return explicitQualified;
  }

  const normalizedStatus = normalizeValue(getLeadStatusValue(conversation));

  if (
    [
      'qualified',
      'qualificado',
      'lead_qualified',
      'enviado ao vendedor',
      'enviado_vendedor',
      'sent_to_sales',
      'sent to sales',
      'chatwoot_handoff_created',
      'handoff_created',
    ].includes(
      normalizedStatus
    )
  ) {
    return true;
  }

  if (
    ['unqualified', 'perdido', 'lost', 'closed_lost', 'closed lost'].includes(
      normalizedStatus
    )
  ) {
    return false;
  }

  return null;
};

const findColumnByAlias = value => {
  const normalizedValue = normalizeValue(value);
  if (!normalizedValue) return null;

  return (
    kanbanColumns.value.find(column =>
      column.aliases.includes(normalizedValue)
    ) || null
  );
};

const findColumnByLabel = label => {
  const normalizedLabel = normalizeValue(label);
  return (
    kanbanColumns.value.find(
      column => normalizeValue(column.label) === normalizedLabel
    ) || kanbanColumns.value[0]
  );
};

const formatLeadStatus = value => {
  const normalizedValue = normalizeValue(value);
  return STATUS_LABELS[normalizedValue] || value || t('LEADS_KANBAN.UNKNOWN');
};

const resolveStage = conversation => {
  const stageValue = getAttributeValue(conversation, stageKeys.value);
  const matchedStage = findColumnByAlias(stageValue);
  if (matchedStage) return matchedStage;

  const rawStatus = getLeadStatusValue(conversation);
  const normalizedStatus = normalizeValue(rawStatus);
  const qualified = resolveQualifiedFlag(conversation);

  if (findColumnByAlias(rawStatus)) {
    return findColumnByAlias(rawStatus);
  }

  if (
    [
      'qualified',
      'qualificado',
      'lead_qualified',
    ].includes(normalizedStatus) ||
    qualified === true
  ) {
    return findColumnByLabel('Qualificado');
  }

  if (
    [
      'waiting_electric_bill',
      'aguardando conta de luz',
      'aguardando conta',
      'conta de luz',
      'aguardando_documento',
      'waiting_for_bill',
    ].includes(normalizedStatus)
  ) {
    return findColumnByLabel('Aguardando conta de luz');
  }

  if (
    [
      'enviado ao vendedor',
      'enviado_vendedor',
      'sent_to_sales',
      'sent to sales',
      'chatwoot_handoff_created',
      'handoff_created',
      'lead_qualified_handoff',
    ].includes(
      normalizedStatus
    )
  ) {
    return findColumnByLabel('Enviado ao vendedor');
  }

  if (
    [
      'follow-up',
      'follow up',
      'followup',
      'retorno',
      'acompanhamento',
      'message_after_qualified',
    ].includes(
      normalizedStatus
    )
  ) {
    return findColumnByLabel('Follow-up');
  }

  if (
    ['lost', 'perdido', 'closed_lost', 'closed lost', 'unqualified'].includes(
      normalizedStatus
    ) ||
    qualified === false
  ) {
    return findColumnByLabel('Perdido');
  }

  return kanbanColumns.value[0];
};

const mapConversationToLead = conversation => {
  const sender = conversation.meta?.sender || {};
  const assignee = conversation.meta?.assignee || {};
  const stage = resolveStage(conversation);
  const leadStatus = getLeadStatusValue(conversation);
  const score = parseScore(getAttributeValue(conversation, scoreKeys.value));
  const qualified = resolveQualifiedFlag(conversation);
  const lastInteraction = conversation.last_activity_at || conversation.timestamp;
  const phone =
    sender.phone_number || getAttributeValue(conversation, phoneKeys.value) || '';
  const origin = getAttributeValue(conversation, sourceKeys.value) || '';

  return {
    id: conversation.id,
    displayId: conversation.display_id,
    stage,
    stageId: stage.id,
    name: sender.name || sender.identifier || `Lead #${conversation.display_id}`,
    phone,
    status: formatLeadStatus(leadStatus),
    conversationStatus: conversation.status || '',
    score,
    qualified,
    lastInteraction,
    origin,
    responsible: assignee.name || t('LEADS_KANBAN.UNASSIGNED'),
    assigneeId: assignee.id || null,
    conversation,
  };
};

const mappedLeads = computed(() =>
  conversations.value.map(conversation => mapConversationToLead(conversation))
);

const filteredLeads = computed(() => {
  const searchTerm = normalizeValue(filters.search);
  const minScore =
    filters.scoreMin === '' ? null : Number.parseFloat(filters.scoreMin);
  const maxScore =
    filters.scoreMax === '' ? null : Number.parseFloat(filters.scoreMax);
  const fromDate = filters.dateFrom
    ? new Date(`${filters.dateFrom}T00:00:00`)
    : null;
  const toDate = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59`) : null;

  return mappedLeads.value.filter(lead => {
    if (searchTerm) {
      const haystack = [
        lead.name,
        lead.phone,
        lead.origin,
        lead.responsible,
        lead.status,
      ]
        .filter(Boolean)
        .map(value => normalizeValue(value))
        .join(' ');

      if (!haystack.includes(searchTerm)) return false;
    }

    if (filters.qualified === 'qualified' && lead.qualified !== true) {
      return false;
    }

    if (filters.qualified === 'unqualified' && lead.qualified !== false) {
      return false;
    }

    if (minScore !== null && (lead.score === null || lead.score < minScore)) {
      return false;
    }

    if (maxScore !== null && (lead.score === null || lead.score > maxScore)) {
      return false;
    }

    if (fromDate || toDate) {
      const lastInteractionDate = lead.lastInteraction
        ? new Date(lead.lastInteraction * 1000)
        : null;

      if (!lastInteractionDate) return false;
      if (fromDate && lastInteractionDate < fromDate) return false;
      if (toDate && lastInteractionDate > toDate) return false;
    }

    return true;
  });
});

const kanbanBoard = computed(() =>
  kanbanColumns.value.map(column => ({
    ...column,
    leads: filteredLeads.value.filter(lead => lead.stageId === column.id),
  }))
);

const activeFilterCount = computed(() =>
  [
    filters.search,
    filters.status,
    filters.assigneeId,
    filters.qualified !== 'all' ? filters.qualified : '',
    filters.scoreMin,
    filters.scoreMax,
    filters.dateFrom,
    filters.dateTo,
  ].filter(Boolean).length
);

const backendFilters = computed(() => {
  const payload = [];

  if (filters.status) {
    payload.push({
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: [filters.status],
      query_operator: 'and',
    });
  }

  if (filters.assigneeId) {
    payload.push({
      attribute_key: 'assignee_id',
      filter_operator: 'equal_to',
      values: [Number(filters.assigneeId)],
      query_operator: 'and',
    });
  }

  if (filters.dateFrom) {
    payload.push({
      attribute_key: 'last_activity_at',
      filter_operator: 'is_greater_than',
      values: [filters.dateFrom],
      query_operator: 'and',
    });
  }

  if (filters.dateTo) {
    payload.push({
      attribute_key: 'last_activity_at',
      filter_operator: 'is_less_than',
      values: [filters.dateTo],
      query_operator: 'and',
    });
  }

  if (payload.length) {
    payload[payload.length - 1].query_operator = undefined;
  }

  return payload;
});

const assigneeOptions = computed(() => agents.value || []);

const clearFilters = () => {
  Object.assign(filters, {
    search: '',
    status: '',
    assigneeId: '',
    qualified: 'all',
    scoreMin: '',
    scoreMax: '',
    dateFrom: '',
    dateTo: '',
  });
};

const fetchConversationPage = async page => {
  if (backendFilters.value.length) {
    const response = await conversationAPI.filter({
      page,
      queryData: {
        payload: backendFilters.value,
      },
    });

    return {
      items: response.data.payload || [],
      totalCount: response.data.meta?.all_count || 0,
    };
  }

  const response = await conversationAPI.get({
    page,
  });

  return {
    items: response.data.data?.payload || [],
    totalCount: response.data.data?.meta?.all_count || 0,
  };
};

const fetchLeads = async () => {
  isLoading.value = true;
  conversations.value = [];

  try {
    const records = [];
    let page = 1;
    let totalCount = 0;

    while (page <= MAX_PAGES) {
      const { items, totalCount: currentTotalCount } =
        await fetchConversationPage(page);

      totalCount = currentTotalCount;
      records.push(...items);

      if (!items.length || records.length >= totalCount) {
        break;
      }

      page += 1;
    }

    conversations.value = records;
    totalLeads.value = totalCount;
  } catch (error) {
    useAlert(t('LEADS_KANBAN.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openConversation = (lead, event) => {
  if (!OPENABLE_STAGE_LABELS.includes(lead.stage?.label)) {
    return;
  }

  const path = frontendURL(
    conversationUrl({
      accountId: accountId.value,
      id: lead.id,
    })
  );

  if (event.metaKey || event.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }

  router.push({ path });
};

const formatLastInteraction = timestamp => {
  if (!timestamp) return t('LEADS_KANBAN.UNKNOWN');
  return shortTimestamp(dynamicTime(timestamp));
};

watch(
  () => [filters.status, filters.assigneeId, filters.dateFrom, filters.dateTo],
  () => {
    fetchLeads();
  }
);

onMounted(async () => {
  await store.dispatch('agents/get');
  await fetchLeads();
});
</script>

<template>
  <section class="flex flex-col flex-1 h-full overflow-hidden bg-n-surface-1">
    <header
      class="sticky top-0 z-10 px-6 py-5 border-b border-n-weak bg-n-alpha-1 backdrop-blur"
    >
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
      >
        <div class="space-y-2">
          <div class="flex items-center gap-3">
            <span
              class="inline-flex items-center justify-center size-10 rounded-2xl bg-n-brand text-white shadow-sm"
            >
              <span class="i-lucide-layout-panel-top size-5" />
            </span>
            <div>
              <h1 class="text-2xl font-semibold text-n-slate-12">
                {{ t('LEADS_KANBAN.TITLE') }}
              </h1>
              <p class="text-sm text-n-slate-10">
                {{ t('LEADS_KANBAN.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="flex flex-wrap gap-2 text-sm text-n-slate-10">
            <span class="px-3 py-1 rounded-full bg-n-alpha-2">
              {{ t('LEADS_KANBAN.TOTAL', { count: totalLeads }) }}
            </span>
            <span class="px-3 py-1 rounded-full bg-n-alpha-2">
              {{
                t('LEADS_KANBAN.FILTERED', { count: filteredLeads.length })
              }}
            </span>
          </div>
        </div>

        <button
          v-if="activeFilterCount"
          class="px-3 py-2 text-sm font-medium transition rounded-xl bg-n-slate-12 text-n-alpha-black2 hover:opacity-90"
          @click="clearFilters"
        >
          {{ t('LEADS_KANBAN.CLEAR_FILTERS') }}
        </button>
      </div>

      <div class="grid gap-3 mt-5 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8">
        <input
          v-model="filters.search"
          type="text"
          class="px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9"
          :placeholder="t('LEADS_KANBAN.FILTERS.SEARCH')"
        />

        <select
          v-model="filters.status"
          class="px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-background text-n-slate-12"
        >
          <option value="">
            {{ t('LEADS_KANBAN.FILTERS.ALL_STATUS') }}
          </option>
          <option value="open">
            {{ t('LEADS_KANBAN.STATUSES.OPEN') }}
          </option>
          <option value="pending">
            {{ t('LEADS_KANBAN.STATUSES.PENDING') }}
          </option>
          <option value="resolved">
            {{ t('LEADS_KANBAN.STATUSES.RESOLVED') }}
          </option>
          <option value="snoozed">
            {{ t('LEADS_KANBAN.STATUSES.SNOOZED') }}
          </option>
        </select>

        <select
          v-model="filters.assigneeId"
          class="px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-background text-n-slate-12"
        >
          <option value="">
            {{ t('LEADS_KANBAN.FILTERS.ALL_RESPONSIBLES') }}
          </option>
          <option
            v-for="agent in assigneeOptions"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }}
          </option>
        </select>

        <select
          v-model="filters.qualified"
          class="px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-background text-n-slate-12"
        >
          <option value="all">
            {{ t('LEADS_KANBAN.FILTERS.ALL_QUALIFICATION') }}
          </option>
          <option value="qualified">
            {{ t('LEADS_KANBAN.FILTERS.QUALIFIED') }}
          </option>
          <option value="unqualified">
            {{ t('LEADS_KANBAN.FILTERS.UNQUALIFIED') }}
          </option>
        </select>

        <input
          v-model="filters.scoreMin"
          type="number"
          min="0"
          class="px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-background text-n-slate-12"
          :placeholder="t('LEADS_KANBAN.FILTERS.MIN_SCORE')"
        />

        <input
          v-model="filters.scoreMax"
          type="number"
          min="0"
          class="px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-background text-n-slate-12"
          :placeholder="t('LEADS_KANBAN.FILTERS.MAX_SCORE')"
        />

        <input
          v-model="filters.dateFrom"
          type="date"
          class="px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-background text-n-slate-12"
        />

        <input
          v-model="filters.dateTo"
          type="date"
          class="px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-background text-n-slate-12"
        />
      </div>
    </header>

    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <Spinner />
    </div>

    <div
      v-else-if="!filteredLeads.length"
      class="flex items-center justify-center flex-1 px-6"
    >
      <div
        class="max-w-md p-8 text-center border shadow-sm rounded-3xl border-n-weak bg-n-background"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('LEADS_KANBAN.EMPTY_STATE.TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-n-slate-10">
          {{ t('LEADS_KANBAN.EMPTY_STATE.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <main
      v-else
      class="flex-1 px-6 py-6 overflow-x-auto overflow-y-hidden bg-[radial-gradient(circle_at_top,_rgba(125,31,200,0.08),_transparent_45%)]"
    >
      <div class="flex gap-5 min-w-max h-full">
        <article
          v-for="column in kanbanBoard"
          :key="column.id"
          class="flex flex-col w-[320px] min-w-[320px] h-full rounded-3xl border border-n-weak bg-n-background/95 shadow-sm"
        >
          <header class="px-4 py-4 border-b border-n-weak">
            <div class="flex items-center justify-between gap-3">
              <div>
                <h2 class="font-semibold text-n-slate-12">
                  {{ column.label }}
                </h2>
                <p class="text-xs text-n-slate-10">
                  {{
                    t('LEADS_KANBAN.COLUMN_COUNT', {
                      count: column.leads.length,
                    })
                  }}
                </p>
              </div>
              <span
                class="px-2.5 py-1 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
              >
                {{ column.leads.length }}
              </span>
            </div>
          </header>

          <div class="flex flex-col gap-3 p-3 overflow-y-auto">
            <button
              v-for="lead in column.leads"
              :key="lead.id"
              class="p-4 text-left transition border shadow-sm rounded-2xl border-n-weak bg-n-surface-1"
              :class="
                OPENABLE_STAGE_LABELS.includes(lead.stage?.label)
                  ? 'hover:border-n-brand hover:-translate-y-0.5 cursor-pointer'
                  : 'opacity-85 cursor-default'
              "
              @click="openConversation(lead, $event)"
            >
              <div class="flex items-start gap-3">
                <Avatar :name="lead.name" :size="40" rounded-full />
                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <h3 class="font-medium truncate text-n-slate-12">
                        {{ lead.name }}
                      </h3>
                      <p class="text-xs text-n-slate-10">
                        #{{ lead.displayId }}
                      </p>
                    </div>
                    <span
                      class="px-2 py-1 text-[11px] font-medium rounded-full bg-n-brand/10 text-n-brand"
                    >
                      {{ lead.score ?? '-' }}
                    </span>
                  </div>

                  <dl class="grid grid-cols-1 gap-2 mt-3 text-sm">
                    <div class="flex items-center justify-between gap-3">
                      <dt class="text-n-slate-10">
                        {{ t('LEADS_KANBAN.CARD.PHONE') }}
                      </dt>
                      <dd class="font-medium text-right text-n-slate-12">
                        {{ lead.phone || t('LEADS_KANBAN.UNKNOWN') }}
                      </dd>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <dt class="text-n-slate-10">
                        {{ t('LEADS_KANBAN.CARD.STATUS') }}
                      </dt>
                      <dd class="font-medium text-right capitalize text-n-slate-12">
                        {{ lead.status }}
                      </dd>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <dt class="text-n-slate-10">
                        {{ t('LEADS_KANBAN.CARD.ORIGIN') }}
                      </dt>
                      <dd class="font-medium text-right text-n-slate-12">
                        {{ lead.origin || t('LEADS_KANBAN.UNKNOWN') }}
                      </dd>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <dt class="text-n-slate-10">
                        {{ t('LEADS_KANBAN.CARD.RESPONSIBLE') }}
                      </dt>
                      <dd class="font-medium text-right text-n-slate-12">
                        {{ lead.responsible }}
                      </dd>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <dt class="text-n-slate-10">
                        {{ t('LEADS_KANBAN.CARD.LAST_INTERACTION') }}
                      </dt>
                      <dd class="font-medium text-right text-n-slate-12">
                        {{ formatLastInteraction(lead.lastInteraction) }}
                      </dd>
                    </div>
                  </dl>

                  <div class="flex items-center gap-2 mt-4">
                    <span
                      class="px-2.5 py-1 text-[11px] rounded-full"
                      :class="
                        lead.qualified === true
                          ? 'bg-[#1aef07]/15 text-[#177a12]'
                          : lead.qualified === false
                            ? 'bg-[#ed6605]/15 text-[#b14b04]'
                            : 'bg-n-alpha-2 text-n-slate-10'
                      "
                    >
                      {{
                        lead.qualified === true
                          ? t('LEADS_KANBAN.CARD.QUALIFIED')
                          : lead.qualified === false
                            ? t('LEADS_KANBAN.CARD.UNQUALIFIED')
                            : t('LEADS_KANBAN.CARD.UNDEFINED')
                      }}
                    </span>
                    <span class="text-xs text-n-slate-10">
                      {{
                        OPENABLE_STAGE_LABELS.includes(lead.stage?.label)
                          ? t('LEADS_KANBAN.CARD.OPEN_CONVERSATION')
                          : t('LEADS_KANBAN.CARD.OPEN_CONVERSATION_BLOCKED')
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </article>
      </div>
    </main>
  </section>
</template>
